/**
 * Shared plumbing for overlay components whose `src` prop is either inline
 * markup or an `ad:`/http URL to fetch (HtmlOverlayLayer).
 */
import { type CSSProperties, useEffect, useState } from "react";
import { useDelayRender } from "remotion";
import { fetchAssetContent } from "../fetch-asset-content";
import { stripQuotes } from "./html-overlay-doc";
import { usePauseWhileLoading } from "./use-pause-while-loading";

/**
 * Off-screen, zero-opacity style for the bitmap preload `<Img>`s. We can't use
 * `display: none` because some browsers defer loading hidden images, which
 * would defeat the preload.
 */
export const HIDDEN_PRELOAD_STYLE: CSSProperties = {
  position: "absolute",
  width: 1,
  height: 1,
  opacity: 0,
  pointerEvents: "none",
};

/**
 * The families of every successfully-loaded FontFace, lowercased. Only counts
 * `loaded` faces: a FontFace in `error` state is technically "registered" but
 * won't actually render, so for missing-font warnings it counts as missing.
 */
export async function loadedFontFamilies(): Promise<Set<string>> {
  await document.fonts.ready;
  return new Set(
    Array.from(document.fonts)
      .filter((f) => f.status === "loaded")
      .map((f) => stripQuotes(f.family).toLowerCase()),
  );
}

/**
 * The markup text behind `src`: the prop itself when it's inline markup, or
 * the fetched body once an `ad:`/http reference resolves (null until then).
 *
 * Inline markup is read directly from the prop on every render so edits
 * propagate without a state sync — storing it in useState would pin the first
 * value for the lifetime of the component. Only fetched markup gates the
 * render with delayRender; embedded bitmap loading is gated separately by the
 * callers' hidden `<Img>` elements, and fonts upstream by `<FontLoader>` in
 * remotion-entry.tsx.
 */
export function useFetchedAssetText(src: string, label: string): string | null {
  const { delayRender, continueRender } = useDelayRender();
  const isUrl = !src.trimStart().startsWith("<");
  const [fetched, setFetched] = useState<string | null>(null);
  const [handle] = useState<number | null>(() =>
    isUrl ? delayRender(label) : null,
  );
  // Settled rather than `fetched !== null`: a failed fetch has to release the
  // Player too, or playback hangs on markup that is never going to arrive.
  const [settled, setSettled] = useState(!isUrl);
  usePauseWhileLoading(!settled);

  // Deliberately independent of the AssetResolver context: adding `resolve`
  // would refetch whenever the context value changes referentially — common
  // when a parent provider recreates the function each render.
  useEffect(() => {
    if (handle === null) return;
    let cancelled = false;

    const loadAndContinue = async () => {
      try {
        const text = await fetchAssetContent(src).then((res) => res.text());
        if (cancelled) return;
        setFetched(text);
      } catch (err) {
        console.error(`${label} failed`, err);
      } finally {
        if (!cancelled) {
          setSettled(true);
          continueRender(handle);
        }
      }
    };

    void loadAndContinue();

    // Unmount is a normal lifecycle event (StrictMode double-mount, parent
    // re-render, etc.) — release the handle so Remotion doesn't wait forever.
    // continueRender, not cancelRender: unmounting isn't an error.
    return () => {
      cancelled = true;
      continueRender(handle);
    };
  }, [handle, src, continueRender, label]);

  return isUrl ? fetched : src;
}
